import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ClueCard from "@/components/feature/ClueCard";
import { categories, clues } from "@/mocks/clueData";
import { useSeo } from "@/hooks/useSeo";

export default function CluesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";
  const [keyword, setKeyword] = useState("");

  useSeo({
    title: "热门爆料列表 - 17.cc网友投稿与匿名爆料合集",
    description:
      "浏览17.cc网友爆料站的投稿合集，涵盖消费维权、职场内幕、城市见闻等主题。支持按分类筛选与关键词搜索，快速找到你关心的17.cc投稿吃瓜内容与线索征集信息。",
    keywords: "17.cc网友爆料,17.cc投稿吃瓜,17.cc用户投稿,爆料列表,线索征集",
    path: "/clues",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "热门爆料列表 - 17.cc 网友爆料站",
        description: "17.cc 网友投稿与匿名爆料合集，支持分类筛选与搜索。",
        url: "https://17.cc/clues",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://17.cc/" },
          { "@type": "ListItem", position: 2, name: "热门爆料", item: "https://17.cc/clues" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: clues.slice(0, 6).map((clue, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: clue.title,
          url: `https://17.cc/clues/${clue.slug}`,
        })),
      },
    ],
  });

  const filtered = useMemo(() => {
    const text = keyword.trim().toLowerCase();
    return clues.filter((clue) => {
      const matchCategory = activeCategory === "all" || clue.categoryId === activeCategory;
      const matchText =
        !text ||
        clue.title.toLowerCase().includes(text) ||
        clue.excerpt.toLowerCase().includes(text) ||
        clue.tags.some((tag) => tag.toLowerCase().includes(text));
      return matchCategory && matchText;
    });
  }, [activeCategory, keyword]);

  const handleCategory = (id: string) => {
    if (id === "all") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", id);
    }
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
      <Breadcrumbs items={[{ label: "热门爆料" }]} />

      <header className="mt-6 max-w-3xl">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
          热门爆料与投稿合集
        </h1>
        <p className="mt-4 text-sm leading-7 text-foreground-600 md:text-base">
          这里汇集了来自网友的投稿与线索。你可以按主题分类筛选，也可以用关键词搜索，
          找到与自己经历相似的那一条。所有内容均经过基本的事实核对与隐私处理。
        </p>
      </header>

      <div className="mt-8 flex flex-col gap-4 border-y border-background-200 py-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleCategory("all")}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
              activeCategory === "all"
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            全部
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => handleCategory(cat.id)}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                activeCategory === cat.id
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <div className="relative w-full lg:w-72">
          <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-500"></i>
          <input
            type="search"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索标题、摘要或标签"
            aria-label="搜索爆料"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <p className="mt-5 text-sm text-foreground-500">
        共找到 <strong className="text-foreground-900">{filtered.length}</strong> 条投稿
      </p>

      {filtered.length > 0 ? (
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((clue) => (
            <ClueCard key={clue.id} clue={clue} />
          ))}
        </div>
      ) : (
        <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-600">
            <i className="ri-search-eye-line text-2xl"></i>
          </span>
          <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
            暂时没有匹配的投稿
          </h2>
          <p className="mt-2 max-w-md text-sm leading-6 text-foreground-600">
            换个关键词试试，或者切换其他主题分类。如果你想补充这个方向的线索，欢迎直接投稿。
          </p>
        </div>
      )}
    </div>
  );
}